import { CORE_API, DASHBOARD_API } from "../constants";
import { getData } from "./common.service";

export type TFarmDataResponse = {
  [key: string]: {
    isCropEnded?: boolean;
    value: number;
    isApplicable?: boolean;
    color?: string;
    minValue: number;
    minOptimalValue?: number;
    maxValue: number;
    maxOptimalValue?: number;
    description: string;
    labels: Array<{
      min: number;
      max: number;
      name: string;
      labelColor: string;
      activeBarColor: string;
    }>;
    stageStartDate?: string[];
    name?: string;
    activeStage?: string;
    variety?: string; // Add variety field
  };
};

// Extended type for ordered data
export type TOrderedFarmDataResponse = TFarmDataResponse & {
  __orderedKeys?: string[];
};

// New API response type for the live-data endpoint
export type TLiveDataResponse = {
  plotId: string;
  name: string;
  area: number;
  deviceType: string;
  latestData: {
    timestamp: string;
    airTemp: number;
    airHumidity: number;
    airPressure: number;
    soilTemp: number;
    soilMoisture1: number;
    soilMoisture2: number;
    leafWetness: number;
    lightIntensity: number;
    windSpeed: number;
    windDirection: string;
    rainFall: number;
    battery: number;
    signalStrength: number;
  };
  sensors: {
    [key: string]: {
      value: number;
      color: string;
      minValue: number;
      minOptimalValue?: number;
      maxValue: number;
      maxOptimalValue?: number;
      description: string;
      labels: Array<{
        min: number;
        max: number;
        name: string;
        labelColor: string;
        activeBarColor: string;
      }>;
    };
  };
  diseases: Array<{
    diseaseId: string;
    isApplicable: boolean;
    oldDiseaseId: string;
    value: number;
    minValue: number;
    maxValue: number;
    description: string;
    labels: Array<{
      min: number;
      max: number;
      name: string;
      labelColor: string;
      activeBarColor: string;
    }>;
    displayName: string;
  }>;
  pests: Array<{
    pestId: string;
    isApplicable: boolean;
    oldPestId: string;
    value: number;
    minValue: number;
    maxValue: number;
    description: string;
    labels: Array<{
      min: number;
      max: number;
      name: string;
      labelColor: string;
      activeBarColor: string;
    }>;
    displayName: string;
  }>;
  crop: {
    created_date: string;
    cropSownId: string;
    cropID: string;
    varietyId: string;
    name: string;
    sowingDate: string;
    activeStage: string;
    isCropEnded: boolean;
    cropStageIds: string[];
    stageStartDate: string[];
    plotId: string;
    cropId: string;
    image: string;
    cropDisplayName: string;
    varietyDisplayName: string;
    stageDisplayName: string;
  };
};

export type DailyWeatherData = {
  createdAt: string;
  modifiedAt: string;
  id: string;
  timestamp: string;
  airTemp: {
    min: { value: number; unit: string };
    max: { value: number; unit: string };
    avg: { value: number; unit: string };
    sourceId: string;
  };
  airHumidity: {
    avg: { value: number; unit: string };
    sourceId: string;
  };
  airPressure: {
    min: { value: number; timestamp: string; unit: string };
    max: { value: number; timestamp: string; unit: string };
    avg: { value: number; unit: string };
    sourceId: string;
  };
  baroPressure: {
    avg: { value: number };
    sourceId: string;
  };
  lightIntensity: {
    avg: { value: number; unit: string };
    max: { value: number; unit: string };
    sourceId: string;
  };
  leafWetness: {
    avg: { value: number };
    sourceId: string;
  };
  windSpeed: {
    avg: { value: number };
    max?: object;
    sourceId: string;
  };
  windDirection: {
    avg: { value: string };
    sourceId: string;
  };
  rainFall: {
    value: number;
    unit: string;
    sourceId: string;
  };
  dayLength: {
    value: number;
    sourceId: string;
  };
  sunshineHours: {
    value: number;
    sourceId: string;
  };
  weatherIcon: {
    value: string;
    sourceId: string;
    displayValue: string;
  };
  iconPhrase: {
    value: string;
    sourceId: string;
  };
  hasPrecipitation: {
    value: boolean;
    sourceId: string;
  };
  precipitationType: {
    value: string;
    sourceId: string;
  };
  precipitationIntensity: {
    value: number;
    unit: string;
    sourceId: string;
  };
  precipitationAccumulation: {
    value: number;
    unit: string;
    sourceId: string;
  };
  precipitationProbability: {
    value: number;
    unit: string;
    sourceId: string;
  };
  cloudCover: {
    value: number;
    sourceId: string;
  };
  locationId: string;
  sourceId: string;
};

type HourlyWeatherData = {
  createdAt: string;
  modifiedAt: string;
  id: string;
  timestamp: string;
  airTemp: {
    value: number;
    unit: string;
    sourceId: string;
  };
  airHumidity: {
    value: number;
    sourceId: string;
  };
  airPressure: {
    value: number;
    sourceId: string;
  };
  baroPressure: {
    value: number;
    sourceId: string;
  };
  lightIntensity: {
    value: number;
    sourceId: string;
  };
  windSpeed: {
    value: number;
    unit: string;
    sourceId: string;
  };
  windDirection: {
    value: string;
    sourceId: string;
  };
  rainFall: {
    value: number;
    unit: string;
    sourceId: string;
  };
  weatherIcon: {
    value: string;
    sourceId: string;
    displayValue: string;
  };
  hasPrecipitation: {
    value: number;
    sourceId: string;
  };
  precipitationType: {
    value: string;
    sourceId: string;
  };
  precipitationIntensity: {
    value: number;
    unit: string;
    sourceId: string;
  };
  precipitationProbability: {
    value: number;
    unit: string;
    sourceId: string;
  };
  precipitationAccumulation: {
    value: number;
    sourceId: string;
  };
  cloudCover: {
    value: number;
    sourceId: string;
  };
  locationId: string;
  sourceId: string;
};



export type TWeatherDataResponse = {
  hourlyWeatherForecastData: HourlyWeatherData[],
  dailyWeatherForecastData: DailyWeatherData[]
}

export const getFarmData = (plotId: string): Promise<TFarmDataResponse> => {
  const url = `/plots/${plotId}/live-data`;

  return getData(url, {
    basePath: CORE_API,
    sendTokenInHeader: true,
  }).then((response: TLiveDataResponse) => {
    // Transform the new API response to match the expected TFarmDataResponse format
    const transformedData: TFarmDataResponse = {};

    // Add sensor data
    if (response.sensors) {
      Object.keys(response.sensors).forEach(sensorKey => {
        const sensorData = response.sensors[sensorKey];
        transformedData[sensorKey] = {
          value: sensorData.value,
          color: sensorData.color,
          minValue: sensorData.minValue,
          minOptimalValue: sensorData.minOptimalValue,
          maxValue: sensorData.maxValue,
          maxOptimalValue: sensorData.maxOptimalValue,
          description: sensorData.description,
          labels: sensorData.labels,
        };
      });
    }

    // Add crop stage data
    if (response.crop) {
      // Debug: Log the crop data to see what fields are available
      
      transformedData.cropStageData = {
        name: response.crop.cropDisplayName, // Use localized display name
        activeStage: response.crop.stageDisplayName, // Use localized stage display name
        isCropEnded: response.crop.isCropEnded,
        stageStartDate: response.crop.stageStartDate,
        variety: response.crop.varietyDisplayName, // Use localized variety display name
        value: 0, // Not applicable for crop stage
        color: "green",
        minValue: 0,
        maxValue: 100,
        description: response.crop.stageDisplayName, // Use localized stage display name
        labels: [],
      };
    }

    // Add timestamp from latestData
    if (response.latestData && response.latestData.timestamp) {
      transformedData.timestamp = {
        value: 0, // Not applicable for timestamp
        color: "gray",
        minValue: 0,
        maxValue: 100,
        description: response.latestData.timestamp,
        labels: [],
      };
    }

    return transformedData;
  });
};

// New function to get disease predictions from the live-data endpoint
export const getDiseasePredictions = (plotId: string): Promise<TFarmDataResponse> => {
  const url = `/plots/${plotId}/live-data`;

  return getData(url, {
    basePath: CORE_API,
    sendTokenInHeader: true,
  }).then((response: TLiveDataResponse) => {
    const transformedData: TFarmDataResponse = {};

    if (response.diseases && Array.isArray(response.diseases)) {
      response.diseases.forEach(disease => {
        if (disease.isApplicable && disease.labels && disease.labels.length > 0) {
          transformedData[disease.diseaseId] = {
            value: disease.value,
            color: disease.displayName,
            minValue: disease.minValue,
            maxValue: disease.maxValue,
            description: disease.description,
            labels: disease.labels,
          };
        }
      });
    }

    return transformedData;
  });
};

// New function to get pest predictions from the live-data endpoint
export const getPestPredictions = (plotId: string): Promise<TFarmDataResponse> => {
  const url = `/plots/${plotId}/live-data`;

  return getData(url, {
    basePath: CORE_API,
    sendTokenInHeader: true,
  }).then((response: TLiveDataResponse) => {
    const transformedData: TFarmDataResponse = {};

    if (response.pests && Array.isArray(response.pests)) {
      response.pests.forEach(pest => {
        if (pest.isApplicable && pest.labels && pest.labels.length > 0) {
          transformedData[pest.pestId] = {
            value: pest.value,
            color: pest.displayName,
            minValue: pest.minValue,
            maxValue: pest.maxValue,
            description: pest.description,
            labels: pest.labels,
          };
        }
      });
    }

    return transformedData;
  });
  
};
export const getWeatherData = (plotId: string): Promise<TWeatherDataResponse>  => {

  const url = "/weather/getLatestWeatherForecast/"+plotId;

  return getData(
    url,
    {
      sendTokenInUrl: true,
      basePath: DASHBOARD_API,
    }
  );
};